import React, { useCallback, useMemo, useRef, useState } from "react";

import { Grid, IconButton, MenuItem } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Divider, Modal } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import classNames from "classnames";
import { AppMenu, AppMenuProps } from "Components/AppMenu";
import { ResourceMenuHandles } from "Components/AppMenu/AppMenu";
import { useOperationDespatchContext } from "../../../../contexts/DespatchContext";
import { PRIVATE_ROUTES } from "Constants";
import { useAppState } from "Context/StateContext";
import { useEventBus } from "Hooks/useEventBus";
import {
  Maximize as IconMaximize,
  Clock as IconClock,
  CloseCircle as IconCloseCircle,
  ArrowUp2 as IconArrowUp,
  Message as IconMessage,
} from "iconsax-react";
import { DespatchTask } from "Models/despatch.task";
import { useTranslation } from "react-i18next";
import { FiPrinter, FiUserPlus } from "react-icons/fi";
import { IconType } from "react-icons/lib";
import { MdOutlineCancel } from "react-icons/md";
import { BusEventPayloads } from "Types";
import { isTaskDisabledToActions, isTaskEnabledToCancel } from "Util/operation.despatch";

import GenerateReportModal, {
  GenerateReportModalHandler,
} from "../../../../../../pages/private/Moving/Shared/GenerateReportModal";
import TaskService from "../../../../../../services/task.service";
import { BusEventNames, TaskStatus, TaskType } from "../../../../../../util/enums";
import { Button } from "../../../../../../components/Form";
import { Color } from "../../../../../../components/Form/Button";
import { StyledMenu } from "../../../../../../components/Generic/AnchorMenu";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { SeeTaskContent } from "../TaskContent";
import { DiarySeeOneProps, DiarySeeOnePopupProps, isTaskAvailableCancel } from "../types";
import { ConfirmationCancelTask } from "./ConfirmationCancel";
import useStyles from "./styles";
import { Task } from "Models/task";

type MenuActionProps = {
  icon: IconType;
  iconColor: string;
  title: string;
  action?: (task: DespatchTask) => void;
  customClasses?: string;
  isDanger?: boolean;
  disabled: boolean;
};

const DiarySeeOneTaskCard = React.forwardRef<any, DiarySeeOneProps>(
  ({ task: _task, onCloseModal }, _ref) => {
    const { t: translate } = useTranslation();
    const classes = useStyles();

    const modalRef = useRef<GenerateReportModalHandler>(null);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [cancelTaskModalOpened, setCancelTaskModalOpened] = useState<boolean>(false);
    const [openAssigning, setOpenAssigning] = useState<boolean>(false);
    const { currentUser } = useAppState();
    const { updateTaskById, isLoadingPersisTaskChanges } = useOperationDespatchContext();

    const [task, setTask] = useState<DespatchTask>(_task);

    const { mutate: cancelTask, isLoading: loadingCancelTask } = useMutation(
      ["task.cancel"],
      ({ id }: DespatchTask) => TaskService.cancel(id),
      {
        onSuccess: () => {
          onCancelSuccess();
        },
      }
    );

    const onSubmitTask = () => {
      updateTaskById(task.id, task);
      onAssignSuccess();
    };

    const onSelectActionInMenu = (action?: MenuActionProps["action"]) => {
      if (action) {
        action(task);
      }
      setAnchorEl(null);
    };

    const { taskCommentRef, despatchTimeControlRef, cancelTaskRef } = useOperationDespatchContext();
    const menuRef = useRef<ResourceMenuHandles | null>(null);

    const isCanAssignLeader = useMemo(() => {
      return ([TaskStatus.REGISTERED, TaskStatus.WAITING_PUBLISH] as string[]).includes(
        task.status
      );
    }, [task.status]);

    const isCanAssignStaffsFleetsAndStorageBoxes = useMemo(() => {
      return (
        [TaskStatus.REGISTERED, TaskStatus.WAITING_PUBLISH, TaskStatus.PENDING_ACCEPT] as string[]
      ).includes(task.status);
    }, [task.status]);

    const isCanAssignTask = useMemo(() => {
      return isCanAssignLeader || isCanAssignStaffsFleetsAndStorageBoxes;
    }, [isCanAssignLeader, isCanAssignStaffsFleetsAndStorageBoxes]);

    const taskOptions: AppMenuProps["actions"] = useMemo(() => {
      if (!task) return [];
      if (task?.isPlaceholder) {
        return [
          {
            label: translate("deletePlaceholderTask"),
            leftContent: <IconCloseCircle size={24} />,
            onClick: () => cancelTaskRef.current?.open(task),
            isError: true,
            isDisabled: !isTaskEnabledToCancel(task),
            tooltipMessage: !isTaskEnabledToCancel(task)
              ? translate("thisTaskCannotBeCancelled")
              : undefined,
          },
        ];
      }

      return isTaskDisabledToActions(task)
        ? undefined
        : [
            {
              label: translate("openOrder"),
              leftContent: <IconMaximize size={24} />,
              onClick: () => {
                const url = `${PRIVATE_ROUTES.ORDERS_MNT}/${task.job.id}`;

                window.open(url);
              },
            },
            // {
            //   label: translate("accessDetails"),
            //   leftContent: <IconMap size={24} />,
            //   onClick: () => {},
            // },
            // {
            //   label: translate("generateReport"),
            //   leftContent: <IconPrinter size={24} />,
            //   onClick: () => {},
            // },
            {
              isDivider: true,
            },

            {
              label: translate("timeControl"),
              leftContent: <IconClock size={24} />,
              onClick: () => {
                despatchTimeControlRef?.current?.open(task?.id);
              },
            },
            {
              label: translate("comments"),
              leftContent: <IconMessage size={24} />,
              rightContent: <div>{task?.comments?.length}</div>,
              onClick: () => taskCommentRef.current?.open(task),
            },
            {
              isDivider: true,
            },

            {
              label: translate("cancelTask"),
              leftContent: <IconCloseCircle size={24} />,
              onClick: () => cancelTaskRef.current?.open(task),
              isError: true,
              isDisabled: !isTaskEnabledToCancel(task),
              tooltipMessage: !isTaskEnabledToCancel(task)
                ? translate("thisTaskCannotBeCancelled")
                : undefined,
            },
          ];
    }, [task]);

    const onAddTaskComment = ({
      comment,
      task: taskUpdated,
    }: BusEventPayloads["despatch:onSuccessCreateComment"]) => {
      if (!comment) return;

      const { id, message, userId, createdAt, updatedAt, taskId, user } = comment;

      if (taskUpdated.id !== task.id) return;

      setTask((prevState) => {
        return {
          ...prevState,
          comments: [
            ...(task.comments ?? []),
            {
              id: id!,
              createdAt: createdAt!,
              message,
              userId,
              user: {
                ...user,
                id: currentUser?.id!,
                fullName: currentUser?.fullName!,
                picture: currentUser?.picture!,
              },
              task,
              taskId,
              updatedAt,
            },
          ],
        };
      });
    };

    const onDeleteTaskComment = ({
      commentId,
      task: taskUpdated,
    }: BusEventPayloads["despatch:onSuccessDeleteComment"]) => {
      if (!commentId || !taskUpdated || !taskUpdated.id) return;

      if (taskUpdated.id !== task.id) return;
      setTask((prevState) => {
        return {
          ...prevState,
          comments: (task.comments ?? []).filter((comment) => comment.id !== commentId),
        };
      });
    };

    useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_CREATE_COMMENT, onAddTaskComment);

    useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_DELETE_COMMENT, onDeleteTaskComment);

    const actionMenuOptions: MenuActionProps[] = useMemo(() => {
      const isCanPrintJob = Boolean(
        (task.type === TaskType.LOCAL ||
          task.type === TaskType.DELIVERY ||
          task.type === TaskType.UPLIFT) &&
          task.status === TaskStatus.FINISHED &&
          task.startDate
      );

      const options: MenuActionProps[] = [
        {
          icon: FiUserPlus,
          iconColor: "#667085",
          title: translate("assignTask"),
          action: () => setOpenAssigning(true),
          disabled: !isCanAssignTask,
        },
        {
          icon: FiPrinter,
          iconColor: "#667085",
          title: translate("printJob"),
          action: () => {
            if (task && task.job && task.job.id) {
              modalRef.current?.handleOpenModal([task.job.id], true);
            }
          },
          disabled: !isCanPrintJob,
        },
      ];

      const isCanCancelTask = isTaskAvailableCancel(task.status);

      options.push({
        icon: MdOutlineCancel,
        iconColor: "#F97066",
        customClasses: classes.itemCancel,
        title: translate("cancelTask"),
        action: () => setCancelTaskModalOpened(true),
        disabled: !isCanCancelTask,
      });

      return options;
    }, [task]);

    const onCancelSuccess = () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      setCancelTaskModalOpened(false);
      onCloseModal();
    };

    const onAssignSuccess = () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      setOpenAssigning(false);
      onCloseModal();
    };

    const onChangeAssign = (value: DespatchTask) => {
      setTask((prev) => {
        return {
          ...prev,
          ...value,
        };
      });
    };

    const dischargTaskChangesAndCancel = useCallback(() => {
      setTask((prev) => {
        return {
          ...prev,
          ..._task,
        };
      });
      setOpenAssigning(false);
    }, [_task]);

    const renderFooter = () => {
      const open = !!anchorEl;

      if (openAssigning) {
        return (
          <Grid
            item
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            className={classes.footer}
          >
            <Grid item>
              <div className={classes.row}>
                <Button
                  customColor={Color.CANCEL}
                  onClick={() => dischargTaskChangesAndCancel()}
                  className={classNames(classes.button)}
                >
                  {translate("cancel")}
                </Button>

                <Button
                  customColor={Color.SUCCESS}
                  onClick={onSubmitTask}
                  className={classNames(classes.button)}
                  loading={isLoadingPersisTaskChanges}
                >
                  {translate("save")}
                </Button>
              </div>
            </Grid>
          </Grid>
        );
      }

      return (
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className={classes.footer}
        >
          <Grid item>
            {isCanAssignTask && (
              <Button
                customColor={Color.BRAND_OUTLINED}
                onClick={(event) => menuRef.current?.open(event.currentTarget)}
                className={classNames(classes.button, classes.actionButton)}
              >
                {translate("actions")}
                <IconArrowUp size={20} color="currentColor" className={classes.icon} />
              </Button>
            )}

            <AppMenu actions={taskOptions} ref={menuRef} />

            <StyledMenu
              id="task-actions"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={() => setAnchorEl(null)}
            >
              {actionMenuOptions.map((actionMenu, index) => (
                <MenuItem
                  key={index}
                  className={classNames(classes.menuItem, actionMenu.customClasses)}
                  onClick={() => onSelectActionInMenu(actionMenu.action)}
                  disabled={actionMenu.disabled}
                >
                  <actionMenu.icon color={actionMenu.iconColor} size={16} />
                  {actionMenu.title}
                </MenuItem>
              ))}
            </StyledMenu>
          </Grid>

          <Grid item>
            <div className={classes.row}>
              <Button
                customColor={Color.GRAY}
                onClick={() => onCloseModal()}
                className={classNames(classes.button)}
              >
                {translate("close")}
              </Button>

              <Button
                customColor={Color.BRAND}
                className={classNames(classes.button)}
                onClick={() => setOpenAssigning(true)}
                disabled={!isCanAssignTask}
              >
                {translate("editTask")}
              </Button>
            </div>
          </Grid>
        </Grid>
      );
    };

    return (
      <div data-cy="seeOneTask" className={classes.container}>
        <Grid container direction="row" justifyContent="space-between" className={classes.wrapper}>
          <Grid
            item
            className={classes.header}
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item className={classes.title}>
              <span>{translate("taskDetails")}</span>
            </Grid>
            <Grid item>
              <IconButton onClick={() => onCloseModal()}>
                <CloseIcon fontSize="medium" />
              </IconButton>
            </Grid>
          </Grid>

          <SeeTaskContent
            task={task}
            wrapLines={false}
            canEdit={openAssigning}
            onEdit={onChangeAssign}
            editPermission={{
              fleets: isCanAssignStaffsFleetsAndStorageBoxes,
              staffs: isCanAssignStaffsFleetsAndStorageBoxes,
              leader: isCanAssignLeader,
              storageBoxes: isCanAssignStaffsFleetsAndStorageBoxes,
            }}
          />

          <Divider flexItem orientation="horizontal" sx={{ width: "100%", marginTop: "32px" }} />

          {renderFooter()}
        </Grid>
        <ConfirmationCancelTask
          modalOpened={cancelTaskModalOpened}
          setModalOpened={setCancelTaskModalOpened}
          onConfirm={cancelTask}
          task={task}
          loading={loadingCancelTask}
        />
        <GenerateReportModal ref={modalRef} />
      </div>
    );
  }
);

export const DiarySeeOneTaskPopup = (props: DiarySeeOnePopupProps) => {
  const { isOpen, onCloseModal } = props;

  return (
    <Modal open={isOpen} onClose={onCloseModal}>
      <DiarySeeOneTaskCard {...props} />
    </Modal>
  );
};
